import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getProductDetail,
  updateProduct,
} from "../../../redux/actions/productActions";
import DefaultLayout from "../../../layouts/DefaultLayout";
import { toast } from "react-toastify";
import { getListSectionsByStore } from "../../../redux/actions/sectionActions";
import Select from "react-select";
import { getListStores } from "../../../redux/actions/storeActions";

// Loading overlay component
const LoadingOverlay = ({ message = "Loading data..." }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 shadow-xl max-w-md w-full">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
          <p className="text-gray-700 text-lg font-medium">{message}</p>
        </div>
      </div>
    </div>
  );
};

function EditProductScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  let { id } = useParams();

  //
  const [productName, setProductName] = useState("");
  const [productNameError, setProductNameError] = useState("");

  const [productImage, setProductImage] = useState("");
  const [productImageValue, setProductImageValue] = useState("");
  const [productImageError, setProductImageError] = useState("");

  const [productDescription, setProductDescription] = useState("");
  const [productDescriptionError, setProductDescriptionError] = useState("");

  const [productPrice, setProductPrice] = useState(0);
  const [productPriceError, setProductPriceError] = useState("");

  const [storeSelect, setStoreSelect] = useState(null);
  const [storeSelectError, setStoreSelectError] = useState("");

  const [sectionSelect, setSectionSelect] = useState(null);
  const [sectionSelectError, setSectionSelectError] = useState("");

  //
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const listStores = useSelector((state) => state.getListStores);
  const { stores, loadingListStores, errorListStores } = listStores;

  const listSectionsByStore = useSelector(
    (state) => state.getListSectionByStore
  );
  const { sections, loadingListSection, errorListSection, pages } =
    listSectionsByStore;

  const productDetail = useSelector((state) => state.detailProduct);
  const {
    loadingProductInfo,
    errorProductInfo,
    successProductInfo,
    productInfo,
  } = productDetail;

  const productUpdate = useSelector((state) => state.updateProduct);
  const { loadingProductUpdate, errorProductUpdate, successProductUpdate } =
    productUpdate;

  const redirect = "/";
  useEffect(() => {
    if (!userInfo) {
      navigate(redirect);
    } else {
      dispatch(getListStores("0"));
      dispatch(getListSectionsByStore("0"));
      dispatch(getProductDetail(id));
    }
  }, [navigate, userInfo, dispatch, id]);

  // State to track data loading and success message
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Effect to handle initial data loading
  useEffect(() => {
    if (productInfo && productInfo !== null && productInfo !== undefined && stores?.length > 0) {
      setProductName(productInfo.product_name ?? "");
      setProductDescription(productInfo.product_description ?? "");
      setProductPrice(productInfo.product_price ?? 0);

      // Handle store selection
      if (productInfo.store) {
        const initialStore = productInfo.store.id ?? "";
        const foundStore = stores.find((item) => item.id === initialStore);

        if (foundStore) {
          const storeOption = {
            value: foundStore.id,
            label: foundStore.store_name,
          };

          setStoreSelect(storeOption);

          // Fetch sections for this store if not already loaded
          dispatch(getListSectionsByStore("0", foundStore.id));
        }
      }

      // Set data loaded to true after initial load
      setIsDataLoaded(true);
    }
  }, [productInfo, stores, dispatch]);

  // Effect to handle section selection after sections are loaded
  useEffect(() => {
    if (isDataLoaded && productInfo?.section && sections?.length > 0) {
      const initialSection = productInfo.section.id ?? "";
      const foundSection = sections.find((item) => item.id === initialSection);

      if (foundSection) {
        setSectionSelect({
          value: foundSection.id,
          label: foundSection.section_name,
        });
      }
    }
  }, [isDataLoaded, productInfo, sections]);

  return (
    <DefaultLayout>
      {/* Show loading overlay when data is being fetched */}
      {(loadingProductInfo || loadingListStores || loadingListSection) && !isDataLoaded && (
        <LoadingOverlay message="Loading product data..." />
      )}

      <div className="animate-fadeIn">
        {/* Success Message */}
        {showSuccessMessage && (
          <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded-md shadow-sm">
            <div className="flex items-center">
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p>Product updated successfully!</p>
            </div>
          </div>
        )}

        {/* Modern Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <a
                href="/dashboard"
                className="inline-flex items-center text-sm font-medium text-primary hover:text-secondary transition-colors duration-200"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  ></path>
                </svg>
                Dashboard
              </a>
            </li>
            <li>
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-secondary"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <a
                  href="/products"
                  className="ml-1 text-sm font-medium text-primary hover:text-secondary md:ml-2 transition-colors duration-200"
                >
                  Products
                </a>
              </div>
            </li>
            <li aria-current="page">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-secondary"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <span className="ml-1 text-sm font-medium text-secondary md:ml-2">
                  Edit Product
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header with background */}
        <div
          className="relative mb-8 rounded-xl overflow-hidden bg-gradient-to-r from-primary to-secondary shadow-lg"
          style={{
            backgroundImage: "url('/assets/background-auth-screen.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-secondary/80"></div>
          <div className="relative z-10 px-6 py-8 md:px-10 md:py-12">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Edit Product
            </h1>
            <p className="text-white/80 max-w-xl">
              Update your product information with all the necessary details.
            </p>
          </div>
        </div>
        {/*  */}
        <div className="rounded-xl border border-stroke bg-whiter px-6 pt-6 pb-5 shadow-sm hover:shadow-md transition-all duration-300 mb-8">
          <div className="container mx-auto flex flex-col">
            {/* Form content */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Product Name */}
              <div>
                <label className="flex items-center text-sm font-medium text-body mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 text-bodydark">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6Z" />
                  </svg>
                  Product Name <span className="text-danger ml-1">*</span>
                </label>
                <div>
                  <input
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      productNameError
                        ? "border-danger bg-danger/5 focus:border-danger"
                        : "border-stroke focus:border-primary bg-white"
                    } outline-none focus:ring-2 ${
                      productNameError ? "focus:ring-danger/20" : "focus:ring-primary/20"
                    } text-sm transition-all duration-200`}
                    type="text"
                    placeholder="Enter product name"
                    value={productName}
                    onChange={(v) => setProductName(v.target.value)}
                  />
                  {productNameError && (
                    <p className="mt-1.5 text-xs text-danger flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {productNameError}
                    </p>
                  )}
                </div>
              </div>

              {/* Product Price */}
              <div>
                <label className="flex items-center text-sm font-medium text-body mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 text-bodydark">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                  </svg>
                  Product Price <span className="text-danger ml-1">*</span>
                </label>
                <div>
                  <input
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      productPriceError
                        ? "border-danger bg-danger/5 focus:border-danger"
                        : "border-stroke focus:border-primary bg-white"
                    } outline-none focus:ring-2 ${
                      productPriceError ? "focus:ring-danger/20" : "focus:ring-primary/20"
                    } text-sm transition-all duration-200`}
                    type="number"
                    placeholder="Enter product price"
                    min={0}
                    step={0.01}
                    value={productPrice}
                    onChange={(v) => setProductPrice(v.target.value)}
                  />
                  {productPriceError && (
                    <p className="mt-1.5 text-xs text-danger flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {productPriceError}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {/*  */}
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              {/* Store Selection */}
              <div>
                <label className="flex items-center text-sm font-medium text-body mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 text-bodydark">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z" />
                  </svg>
                  Select Store <span className="text-danger ml-1">*</span>
                </label>
                <div>
                  <Select
                    value={storeSelect}
                    onChange={(option) => {
                      setStoreSelect(option);
                      setSectionSelect(null);
                      setSectionSelectError("");

                      // filter
                      if (option && option.value) {
                        dispatch(getListSectionsByStore("0", option.value));
                      } else {
                        dispatch({ type: "RESET_SECTION" });
                      }
                    }}
                    options={stores?.map((store) => ({
                      value: store.id,
                      label: store.store_name,
                    }))}
                    filterOption={(option, inputValue) =>
                      option.label
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                    }
                    className="text-sm"
                    placeholder="Select a Store..."
                    isSearchable
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        background: "#fff",
                        borderRadius: '0.5rem',
                        padding: '2px',
                        border: storeSelectError
                          ? "2px solid #d34053"
                          : state.isFocused
                            ? "2px solid #0D9F61"
                            : "2px solid #e2e8f0",
                        boxShadow: state.isFocused ? "0 0 0 1px rgba(13, 159, 97, 0.1)" : "none",
                        "&:hover": {
                          border: state.isFocused
                            ? "2px solid #0D9F61"
                            : "2px solid #cbd5e1",
                        },
                        transition: 'all 0.2s ease',
                      }),
                      option: (base, state) => ({
                        ...base,
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: state.isSelected
                          ? 'rgba(13, 159, 97, 0.1)'
                          : state.isFocused
                            ? 'rgba(13, 159, 97, 0.05)'
                            : 'transparent',
                        color: state.isSelected ? '#0D9F61' : base.color,
                        '&:hover': {
                          backgroundColor: 'rgba(13, 159, 97, 0.05)',
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        display: "flex",
                        alignItems: "center",
                        color: '#1e293b',
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: '#94a3b8',
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        color: '#94a3b8',
                        '&:hover': {
                          color: '#0D9F61',
                        },
                      }),
                    }}
                  />
                  {storeSelectError && (
                    <p className="mt-1.5 text-xs text-danger flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {storeSelectError}
                    </p>
                  )}
                </div>
              </div>

              {/* Section Selection */}
              <div>
                <label className="flex items-center text-sm font-medium text-body mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 text-bodydark">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 0 1 0-3.75Z" />
                  </svg>
                  Select Section <span className="text-danger ml-1">*</span>
                  <a href="/sections" className="ml-2 text-xs text-primary hover:text-secondary transition-colors duration-200 inline-flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 mr-0.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Create New
                  </a>
                </label>
                <div>
                  <Select
                    value={sectionSelect}
                    onChange={(option) => {
                      setSectionSelect(option);
                    }}
                    options={sections?.map((store) => ({
                      value: store.id,
                      label: store.section_name,
                    }))}
                    filterOption={(option, inputValue) =>
                      option.label
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                    }
                    className="text-sm"
                    placeholder="Select a Section..."
                    isSearchable
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        background: "#fff",
                        borderRadius: '0.5rem',
                        padding: '2px',
                        border: sectionSelectError
                          ? "2px solid #d34053"
                          : state.isFocused
                            ? "2px solid #0D9F61"
                            : "2px solid #e2e8f0",
                        boxShadow: state.isFocused ? "0 0 0 1px rgba(13, 159, 97, 0.1)" : "none",
                        "&:hover": {
                          border: state.isFocused
                            ? "2px solid #0D9F61"
                            : "2px solid #cbd5e1",
                        },
                        transition: 'all 0.2s ease',
                      }),
                      option: (base, state) => ({
                        ...base,
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: state.isSelected
                          ? 'rgba(13, 159, 97, 0.1)'
                          : state.isFocused
                            ? 'rgba(13, 159, 97, 0.05)'
                            : 'transparent',
                        color: state.isSelected ? '#0D9F61' : base.color,
                        '&:hover': {
                          backgroundColor: 'rgba(13, 159, 97, 0.05)',
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        display: "flex",
                        alignItems: "center",
                        color: '#1e293b',
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: '#94a3b8',
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        color: '#94a3b8',
                        '&:hover': {
                          color: '#0D9F61',
                        },
                      }),
                    }}
                  />
                  {sectionSelectError && (
                    <p className="mt-1.5 text-xs text-danger flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {sectionSelectError}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {/*  */}
            {/* Product Description */}
            <div className="mt-6">
              <label className="flex items-center text-sm font-medium text-body mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 text-bodydark">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
                </svg>
                Product Description <span className="text-danger ml-1">*</span>
              </label>
              <div>
                <textarea
                  rows={5}
                  placeholder="Enter product description"
                  className={`w-full px-4 py-3 rounded-lg border-2 ${
                    productDescriptionError
                      ? "border-danger bg-danger/5 focus:border-danger"
                      : "border-stroke focus:border-primary bg-white"
                  } outline-none focus:ring-2 ${
                    productDescriptionError ? "focus:ring-danger/20" : "focus:ring-primary/20"
                  } text-sm transition-all duration-200`}
                  value={productDescription}
                  onChange={(v) => setProductDescription(v.target.value)}
                ></textarea>
                {productDescriptionError && (
                  <p className="mt-1.5 text-xs text-danger flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {productDescriptionError}
                  </p>
                )}
              </div>
            </div>

            {/* Product Image */}
            <div className="mt-6">
              <label className="flex items-center text-sm font-medium text-body mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 text-bodydark">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                </svg>
                Product Image <span className="text-secondary text-xs ml-1">(Optional)</span>
              </label>
              <div>
                <div
                  className={`border-2 rounded-lg p-4 ${
                    productImageError
                      ? "border-danger bg-danger/5"
                      : "border-stroke bg-gray-1 border-dashed"
                  }`}
                  onClick={(e) => {
                    // Only trigger file input click if the user explicitly clicks on this container
                    if (e.currentTarget === e.target || e.target.closest('.file-upload-content')) {
                      document.getElementById('product-image-upload').click();
                    }
                  }}
                >
                  <input
                    id="product-image-upload"
                    type="file"
                    className="hidden"
                    value={productImageValue}
                    onChange={(v) => {
                      setProductImageValue(v.target.value);
                      setProductImage(v.target.files[0]);
                    }}
                  />
                  <div className="flex flex-col items-center justify-center text-center file-upload-content">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-bodydark mb-2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                    </svg>
                    <p className="text-sm font-medium text-body">
                      {productImageValue ? productImageValue.split('\\').pop() : "Drag & drop your image here or click to browse"}
                    </p>
                    <p className="text-xs text-bodydark mt-1">
                      Leave empty to keep the current image
                    </p>
                  </div>
                </div>
                {productImageError && (
                  <p className="mt-1.5 text-xs text-danger flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {productImageError}
                  </p>
                )}
              </div>
            </div>
            {/*  */}
            {/* Action Buttons */}
            <div className="mt-8 border-t border-stroke pt-6">
              <div className="flex flex-row items-center justify-end gap-3">
                <a
                  href="/products"
                  className="px-5 py-2.5 rounded-lg border-2 border-stroke text-bodydark font-medium text-sm hover:bg-gray-1 transition-colors duration-200 flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
                  </svg>
                  Cancel
                </a>
                <button
                  disabled={loadingProductUpdate}
                  onClick={async () => {
                    var check = true;
                    setProductNameError("");
                    setProductImageError("");
                    setProductDescriptionError("");
                    setProductPriceError("");
                    setStoreSelectError("");
                    setSectionSelectError("");

                    if (productName === "") {
                      setProductNameError("This field is required.");
                      check = false;
                    }
                    if (productDescription === "") {
                      setProductDescriptionError("This field is required.");
                      check = false;
                    }
                    if (productPrice === "") {
                      setProductPriceError("This field is required.");
                      check = false;
                    }

                    // Check if store is selected
                    if (!storeSelect) {
                      setStoreSelectError("This field is required.");
                      check = false;
                    }

                    // Check if section is selected
                    if (!sectionSelect) {
                      setSectionSelectError("This field is required.");
                      check = false;
                    }

                    if (check) {
                      try {
                        dispatch(
                          updateProduct(id, {
                            product_name: productName,
                            product_image: productImage,
                            product_store: storeSelect.value,
                            product_section: sectionSelect.value,
                            product_description: productDescription,
                            product_price: productPrice,
                          })
                        );

                        // Show success message
                        setShowSuccessMessage(true);

                        // Hide success message after 5 seconds
                        setTimeout(() => {
                          setShowSuccessMessage(false);
                        }, 5000);

                        // Refresh product data
                        dispatch(getProductDetail(id));
                      } catch (error) {
                        toast.error("Failed to update product. Please try again.");
                      }
                    } else {
                      toast.error(
                        "Some fields are empty or invalid. Please try again."
                      );
                    }
                  }}
                  className="px-5 py-2.5 rounded-lg bg-primary text-white font-medium text-sm hover:bg-primary/90 transition-colors duration-200 shadow-sm hover:shadow-md flex items-center"
                >
                  {loadingProductUpdate ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Updating...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
                      </svg>
                      Update Product
                    </>
                  )}
                </button>
              </div>
            </div>

            {/*  */}
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
}

export default EditProductScreen;
